import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/primitives/Dialog';

import {
  EXAM_EXTENSION_OPTIONS,
  EXAM_FOCUS_OPTIONS,
  EXAM_LEVEL_OPTIONS,
  buildGuideGenerationPrompt,
  buildCurriculumIntentDraft,
  defaultGuideRecipeConfigForFocus,
  GUIDE_INTENT_QUICK_PRESETS,
  guideRecipeModeFromFocus,
  GUIDE_LENGTH_OPTIONS,
  guideLengthHintForFocus,
  isQuestionnaireFocus,
  type CurriculumTrace,
  type ExamAiBuildOptions,
  type ExamExtensionId,
  type ExamFocusId,
  type ExamLevelId,
  type GuideLengthId,
  type GuideRecipeConfig,
  type QuestionnaireDelivery,
  type ScoreContextPayload,
} from '../constants/examAiParams';

import { enfoqueFromExamFocus, previewGuideBlueprint, suggestCurriculumTargetFromPrompt, type CurriculumTarget, type EnfoqueDidactico, type GuideBlueprintPreview, type GuideGenerationMode } from '@interactive-room';

import { resolveMusicGenerationMode } from '../constants/examAiParams';

import { MusicMethodologyPanel } from './MusicMethodologyPanel';

import { ScoreUploadPanel } from './ScoreUploadPanel';

import { GuideMaterialUploadPanel } from './GuideMaterialUploadPanel';

import { GuideCurriculumPanel } from './GuideCurriculumPanel';

import { GuidePlanningTrace } from './GuidePlanningTrace';
import { SessionContextInferencePanel } from './planning/SessionContextInferencePanel';
import { ExamAiConfigSidebar } from './ExamAiConfigSidebar';

import { DEFAULT_MUSIC_AI_PREFS } from '../constants/musicAiParams';

import type { MusicGenerationPrefs, ParsedScoreAst } from '@interactive-room';

import { analyzeScoreFromAst } from '@interactive-room';

import type { GuideKnowledgeBasePayload } from '../api/materialContextApi';
import type { MaterialIntent } from '../constants/materialIntent';
import { MATERIAL_INTENT_OPTIONS } from '../constants/materialIntent';
import { deliveryProfileFromIntent } from '../constants/deliveryProfile';
import type { DeliveryProfile } from '../constants/deliveryProfile';
import {
  curriculumUnavailableMessage,
  hasCurriculumSubjectSupport,
} from '../constants/subjectsWithCurriculum';
import { buildMaterialSeedFromLinkedSession } from '../types/linkedSessionContext';
import { requireMaterialLevelAndSubject } from '../lib/courseProfileValidation';
interface ExamAiBuildDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subjectLabel?: string;
  subjectId?: string;
  /** Tag canónico de asignatura (ej. matematica) — fallback si subjectId no llega. */
  subjectTag?: string;
  resourceType?: 'exam' | 'guide';
  /** Modelo 2 — ficha imprimible vs clase proyectable. */
  materialIntent?: MaterialIntent;
  /** Contexto de sesión planificada — pre-rellena intención y alinea generación IA. */
  linkedSessionContext?: import('../types/linkedSessionContext').LinkedSessionContext;
  /** Intención docente del wizard global (sin sesión vinculada). */
  teacherPromptSeed?: string;
  /** Curso activo (fallback de nivel/asignatura cuando no hay linkedSessionContext). */
  courseProfile?: import('../types/courseProfile').CourseProfile | null;
  loading?: boolean;
  onConfirm: (options: ExamAiBuildOptions) => void;
}

function SegmentedRow<T extends string>({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: T;
  options: Array<{ id: T; icon?: string; label: string; detail?: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="space-y-2">
      <div>
        <p className="sala-label">{label}</p>
        {hint && <p className="text-[10px] text-[var(--sala-ink-dim)]">{hint}</p>}
      </div>
      <div className="studio-tabs flex-wrap">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            className={`studio-tabs__btn whitespace-nowrap ${value === option.id ? 'studio-tabs__btn--active' : ''}`}
            onClick={() => onChange(option.id)}
            title={option.detail}
          >
            {option.icon ? `${option.icon} ` : ''}
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ExamAiBuildDialog({
  open,
  onOpenChange,
  subjectLabel,
  subjectId,
  subjectTag: subjectTagProp,
  resourceType = 'exam',
  materialIntent = 'print',
  linkedSessionContext,
  teacherPromptSeed,
  courseProfile,
  loading = false,
  onConfirm,
}: ExamAiBuildDialogProps) {
  const isGuide = resourceType === 'guide';
  const isInteractiveClass = materialIntent === 'interactive' && !isGuide;
  const isLearningGuideProfile = materialIntent === 'print' && isGuide;
  const selectedIntent = MATERIAL_INTENT_OPTIONS.find((option) => option.id === materialIntent);
  const baseDeliveryProfile = deliveryProfileFromIntent(materialIntent);

  // Sin default 'general'/'5basico': si faltan, bloqueamos generar (fail-loud).
  const subjectTag =
    subjectTagProp ?? subjectId ?? linkedSessionContext?.subjectTag ?? courseProfile?.subjectTag ?? '';
  const subjectHasMatrix = hasCurriculumSubjectSupport(subjectTag);
  const hasCurriculumSupport = Boolean(subjectTag) && isGuide && subjectHasMatrix;
  const [prompt, setPrompt] = useState('');
  const [contentContext, setContentContext] = useState('');
  const [extension, setExtension] = useState<ExamExtensionId>(isInteractiveClass ? 'ephemeral' : 'short');
  const [level, setLevel] = useState<ExamLevelId | ''>(
    () => (courseProfile?.level as ExamLevelId | undefined) ?? '',
  );
  const [focus, setFocus] = useState<ExamFocusId>(isGuide ? 'learning' : 'practical');
  const [questionnaireDelivery, setQuestionnaireDelivery] =
    useState<QuestionnaireDelivery>('printable');
  const [guideConfig, setGuideConfig] = useState<GuideRecipeConfig>(() =>
    defaultGuideRecipeConfigForFocus(isGuide ? 'learning' : 'practical'),
  );
  const [musicPrefs, setMusicPrefs] = useState<MusicGenerationPrefs>(DEFAULT_MUSIC_AI_PREFS);
  const [scoreAst, setScoreAst] = useState<ParsedScoreAst | null>(null);
  const [knowledgeBase, setKnowledgeBase] = useState<GuideKnowledgeBasePayload | null>(null);
  const isMusic = subjectTag === 'musica';
  const musicMode = isMusic
    ? resolveMusicGenerationMode(scoreAst?.summary ? { summary: scoreAst.summary } : null)
    : null;
  const isRepertoireMode = musicMode === 'repertoire';
  const selectedExtension = EXAM_EXTENSION_OPTIONS.find((option) => option.id === extension)!;
  const [blueprintPreview, setBlueprintPreview] = useState<GuideBlueprintPreview | null>(null);
  const [blueprintLoading, setBlueprintLoading] = useState(false);
  const [generationMode, setGenerationMode] = useState<GuideGenerationMode>('curricular');
  const [curriculumTarget, setCurriculumTarget] = useState<CurriculumTarget | null>(null);
  const [curriculumTrace, setCurriculumTrace] = useState<CurriculumTrace | null>(null);
  const curriculumTouchedRef = useRef(false);
  const autoCurriculumAppliedRef = useRef(false);
  const [curriculumSuggestionNote, setCurriculumSuggestionNote] = useState<string | null>(null);
  const intentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const linkedSessionSeededRef = useRef(false);
  const [inheritedCurriculum, setInheritedCurriculum] = useState(false);

  // Sin matriz MINEDUC: nunca dejar generationMode en curricular (evita curriculum_not_justified).
  useEffect(() => {
    if (!isGuide || subjectHasMatrix) return;
    setGenerationMode('independent');
    setCurriculumTarget(null);
    setInheritedCurriculum(false);
  }, [isGuide, subjectHasMatrix]);
  useEffect(() => {
    if (!open || linkedSessionSeededRef.current) return;
    const wizardSeed = teacherPromptSeed?.trim() || '';
    const sessionSeed = linkedSessionContext
      ? buildMaterialSeedFromLinkedSession(linkedSessionContext)
      : '';
    // Intención del wizard tiene prioridad; el hito/contexto viaja en linkedSessionContext a la API.
    const seed = wizardSeed || sessionSeed;
    if (seed) {
      if (isGuide) {
        setContentContext((prev) => (prev.trim() ? prev : seed));
      } else {
        setPrompt((prev) => (prev.trim() ? prev : seed));
      }
    }

    const seededLevel = linkedSessionContext?.level ?? courseProfile?.level;
    if (seededLevel) {
      setLevel(seededLevel as ExamLevelId);
    }

    const inherited = linkedSessionContext?.curriculumTarget;
    const inheritedMode = linkedSessionContext?.generationMode ?? 'curricular';
    // Antes exigíamos axis+oaCode+oahCode+oaaCode completos o descartábamos toda la
    // herencia. Ahora basta eje+OA válidos — GuideCurriculumPanel completa OAH/OAA
    // faltantes en vez de resetear al primer eje de la matriz.
    // Sin matriz oficial: no heredar curricular (historia/ciencias, etc.).
    if (
      subjectHasMatrix &&
      inherited?.axis &&
      inherited.oaCode &&
      inheritedMode !== 'independent'
    ) {
      setGenerationMode('curricular');
      setCurriculumTarget(inherited);
      curriculumTouchedRef.current = true;
      autoCurriculumAppliedRef.current = false;
      setCurriculumSuggestionNote(null);
      setInheritedCurriculum(true);
    } else if (isGuide && !subjectHasMatrix) {
      setGenerationMode('independent');
      setCurriculumTarget(null);
      setInheritedCurriculum(false);
    }

    linkedSessionSeededRef.current = true;
  }, [open, linkedSessionContext, teacherPromptSeed, isGuide, subjectHasMatrix, courseProfile?.level]);
  useEffect(() => {
    if (open) return;
    linkedSessionSeededRef.current = false;
    autoCurriculumAppliedRef.current = false;
    setCurriculumSuggestionNote(null);
    setInheritedCurriculum(false);
  }, [open]);
  const curricularReady =
    !hasCurriculumSupport ||
    generationMode === 'independent' ||
    Boolean(
      curriculumTarget?.axis &&
        curriculumTarget?.oaCode &&
        curriculumTarget?.oahCode &&
        curriculumTarget?.oaaCode,
    );
  const intentText = isGuide || isQuestionnaireFocus(focus)
    ? contentContext.trim() || prompt.trim()
    : prompt.trim();
  const previewSeed =
    intentText ||
    linkedSessionContext?.sessionObjective?.trim() ||
    (generationMode === 'curricular' ? curriculumTrace?.oaSummary ?? '' : '');
  const previewCurriculumTarget =
    curriculumTarget ?? linkedSessionContext?.curriculumTarget ?? undefined;
  const previewLevel = (linkedSessionContext?.level as ExamLevelId | undefined) ?? level;
  const identityGate = requireMaterialLevelAndSubject({
    level: linkedSessionContext?.level ?? level,
    subjectTag,
  });
  const profileIdentityReady = identityGate.ok;
  useEffect(() => {
    if (inheritedCurriculum) return;
    setCurriculumTarget(null);
    setCurriculumTrace(null);
    curriculumTouchedRef.current = false;
    autoCurriculumAppliedRef.current = false;
    setCurriculumSuggestionNote(null);
  }, [level, inheritedCurriculum]);

  // Auto-elige el OA/eje que mejor justifica el pedido docente (matriz completa del curso).
  useEffect(() => {
    if (!open || !isGuide || !hasCurriculumSupport || generationMode === 'independent') return;
    if (inheritedCurriculum || curriculumTouchedRef.current) return;
    if (autoCurriculumAppliedRef.current) return;
    const promptText = (contentContext.trim() || teacherPromptSeed?.trim() || '').trim();
    if (promptText.length < 4 || !profileIdentityReady || !previewLevel) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const suggestion = await suggestCurriculumTargetFromPrompt({
          prompt: promptText,
          level: previewLevel,
          subjectTag,
        });
        if (cancelled) return;
        if (inheritedCurriculum || curriculumTouchedRef.current) return;
        if (!suggestion?.curriculumTarget?.axis || !suggestion.curriculumTarget.oaCode) return;
        setCurriculumTarget(suggestion.curriculumTarget);
        autoCurriculumAppliedRef.current = true;
        const summary = suggestion.matchedOaText.replace(/\s+/g, ' ').trim().slice(0, 120);
        setCurriculumSuggestionNote(
          `Sugerido desde tu intención: ${suggestion.curriculumTarget.oaCode}${
            summary ? ` — ${summary}${suggestion.matchedOaText.length > 120 ? '…' : ''}` : ''
          }`,
        );
      } catch {
        /* servidor caído o HTML de SPA — no bloquear el modal */
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    open,
    isGuide,
    hasCurriculumSupport,
    generationMode,
    contentContext,
    teacherPromptSeed,
    previewLevel,
    subjectTag,
    profileIdentityReady,
    inheritedCurriculum,
  ]);
  useEffect(() => {
    const suggested = blueprintPreview?.suggestedCurriculumTarget;
    if (!suggested?.axis || !suggested.oaCode) return;
    if (inheritedCurriculum || curriculumTouchedRef.current) return;
    // Prioriza la sugerencia por prompt (matriz completa); blueprint solo rellena si aún no hay OA.
    if (curriculumTarget?.oaCode) return;
    setCurriculumTarget(suggested);
  }, [blueprintPreview?.suggestedCurriculumTarget, curriculumTarget?.oaCode, inheritedCurriculum]);
  useEffect(() => {
    if (!open || !isGuide || !hasCurriculumSupport || generationMode === 'independent' || !previewSeed || !profileIdentityReady) {
      if (!open) setBlueprintPreview(null);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setBlueprintLoading(true);
      try {
        const preview = await previewGuideBlueprint({
          contentContext: previewSeed,
          level: previewLevel,
          subjectTag,
          extension,
          generationMode,
          guideLength: guideConfig.guideLength ?? 'medium',
          curriculumTarget: previewCurriculumTarget,
          linkedSessionContext: linkedSessionContext ?? undefined,
        });
        if (!cancelled) setBlueprintPreview(preview);
      } catch {
        if (!cancelled) setBlueprintPreview(null);
      } finally {
        if (!cancelled) setBlueprintLoading(false);
      }

    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    open,
    isGuide,
    hasCurriculumSupport,
    previewSeed,
    previewLevel,
    extension,
    generationMode,
    previewCurriculumTarget,
      guideConfig.guideLength ?? 'medium',
    guideConfig.includeActivities,
    focus,
    subjectTag,
    linkedSessionContext,
    profileIdentityReady,
  ]);
  const selectedGuideLength =
    GUIDE_LENGTH_OPTIONS.find((option) => option.id === (guideConfig.guideLength ?? 'medium')) ??
    GUIDE_LENGTH_OPTIONS[1];
  const isQuestionnaire = isQuestionnaireFocus(focus);
  // Cuestionario = simulación formativa de práctica — pipeline de guía, nunca evaluación.
  // Etiqueta/producto = cuestionario (focus inquiry); no teach→practice de guía de aprendizaje.
  const effectiveResourceType: 'exam' | 'guide' = isQuestionnaire ? 'guide' : resourceType;
  const effectiveIsGuide = effectiveResourceType === 'guide';
  const deliveryProfile: DeliveryProfile = isQuestionnaire
    ? 'learning_guide'
    : baseDeliveryProfile;
  const effectiveIsInteractiveClass =
    !isQuestionnaire &&
    (deliveryProfile === 'interactive_class' || isInteractiveClass);
  const effectiveBlockCount = effectiveIsGuide
    ? blueprintPreview?.blockCount ?? selectedGuideLength.blockCount
    : blueprintPreview?.blockCount ?? selectedExtension.exactBlocks;
  const guideLengthHint = effectiveIsGuide
    ? guideLengthHintForFocus(selectedGuideLength, focus, effectiveBlockCount)
    : '';
  const handleFocusChange = (nextFocus: ExamFocusId) => {
    setFocus(nextFocus);
    if (isGuide || isQuestionnaireFocus(nextFocus)) {
      setGuideConfig(defaultGuideRecipeConfigForFocus(nextFocus));
    }
  };
  const handleTraceChange = useCallback((trace: CurriculumTrace | null) => {
    setCurriculumTrace(trace);
  }, []);
  const handleSuggestCurriculumIntent = () => {
    if (!curriculumTarget || !curricularReady) return;
    const draft = buildCurriculumIntentDraft(
      curriculumTarget,
      level,
      subjectTag,
      curriculumTrace,
      subjectLabel,
    );
    setContentContext(draft);
    window.requestAnimationFrame(() => intentTextareaRef.current?.focus());
  };
  const resetForm = () => {
    setPrompt('');
    setContentContext('');
    setScoreAst(null);
    setKnowledgeBase(null);
    setGenerationMode('curricular');
    setCurriculumTarget(null);
    setCurriculumTrace(null);
    setInheritedCurriculum(false);
  };
  const handleConfirm = () => {
    const baseIntent = contentContext.trim() || prompt.trim();
    const trimmedIntent = effectiveIsGuide
      ? isQuestionnaire
        ? [
            'Cuestionario de práctica (simulación formativa, sin calificación).',
            'Genera solo ítems respondibles (completar, cuentas, problemas con cantidades concretas).',
            'PROHIBIDO enseñar el concepto como guía de aprendizaje.',
            baseIntent,
          ]
            .filter(Boolean)
            .join('\n')
        : buildGuideGenerationPrompt(baseIntent)
      : prompt.trim();
    if (!trimmedIntent) return;
    const resolvedIdentity = requireMaterialLevelAndSubject({
      level: linkedSessionContext?.level ?? level,
      subjectTag,
    });
    if (!resolvedIdentity.ok) return;
    const scoreSummary = scoreAst
      ? (scoreAst.summary ?? analyzeScoreFromAst(scoreAst, musicPrefs))
      : undefined;
    const inheritedTarget =
      effectiveIsGuide && !subjectHasMatrix
        ? undefined
        : linkedSessionContext?.generationMode !== 'independent'
          ? linkedSessionContext?.curriculumTarget
          : undefined;
    const resolvedGenerationMode =
      effectiveIsGuide && !subjectHasMatrix
        ? 'independent'
        : hasCurriculumSupport
          ? generationMode
          : inheritedTarget
            ? (linkedSessionContext?.generationMode ?? 'curricular')
            : undefined;
    const resolvedCurriculumTarget =
      effectiveIsGuide && !subjectHasMatrix
        ? undefined
        : hasCurriculumSupport && generationMode === 'curricular' && curriculumTarget
          ? curriculumTarget
          : inheritedTarget;
    onConfirm({
      prompt: trimmedIntent,
      ...(effectiveIsGuide && (hasCurriculumSupport || linkedSessionContext || isQuestionnaire)
        ? { contentContext: trimmedIntent }
        : {}),
      extension,
      level: resolvedIdentity.level,
      focus,
      enfoqueDidactico: isQuestionnaire ? 'practico' : enfoqueFromExamFocus(focus),
      ...(effectiveIsGuide
        ? {
            mode: guideRecipeModeFromFocus(focus),
            config: guideConfig,
            guideLength: guideConfig.guideLength ?? 'medium',
          }

        : {}),
      ...(resolvedGenerationMode ? { generationMode: resolvedGenerationMode } : {}),
      ...(resolvedCurriculumTarget ? { curriculumTarget: resolvedCurriculumTarget } : {}),
      ...(effectiveIsGuide && knowledgeBase ? { knowledgeBase } : {}),

      resourceType: effectiveResourceType,
      deliveryFormat: effectiveIsInteractiveClass
        ? 'interactive'
        : effectiveIsGuide
          ? 'print'
          : undefined,
      deliveryProfile,
      ...(isMusic
        ? {
            musicPrefs,
            scoreContext: scoreSummary
              ? ({
                  title: scoreAst!.title,
                  summary: scoreSummary,
                  generationMode: 'repertoire',
                  primaryPattern: scoreAst!.primaryPattern,
                  timeSignature: scoreAst!.timeSignature,
                  bpm: scoreAst!.bpm,
                  melodyNotes: scoreAst!.melodyNotes,
                } satisfies ScoreContextPayload)
              : null,
          }

        : {}),
      ...(linkedSessionContext ? { linkedSessionContext } : {}),
    });
    resetForm();
    onOpenChange(false);
  };
  const intentPlaceholder = hasCurriculumSupport
    ? generationMode === 'curricular' && curriculumTrace?.oaSummary
      ? `Ej: ${curriculumTrace.oaSummary.slice(0, 80)}… con autos en el recreo, saltos de 2 en 2 hasta 20, espacio al final para explicar cómo contaron.`
      : 'Ej: Conteo por saltos de 2 en 2 y 5 en 5 con metáfora lúdica, hasta el 20, con espacio para que expliquen su estrategia.'
    : 'Ej: Unidad de fracciones — introducción conceptual, ejemplo resuelto y ejercicios de aplicación';
  const canSubmit =
    intentText.length >= 8 &&
    !loading &&
    profileIdentityReady &&
    (!hasCurriculumSupport || curricularReady);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sala-dialog--studio sala-dialog--ai sala-dialog--ai-wide sala-dialog--micelio"
        a11yTitle={
          isQuestionnaire
            ? 'Configurar cuestionario'
            : effectiveIsGuide
              ? 'Configurar material'
              : effectiveIsInteractiveClass
                ? 'Configurar clase interactiva'
                : 'Configurar evaluación'
        }
      >
        <DialogHeader>
          <DialogTitle>
            {isQuestionnaire
              ? 'Configurar cuestionario'
              : effectiveIsGuide
                ? 'Configurar material'
                : effectiveIsInteractiveClass
                  ? 'Configurar clase interactiva'
                  : 'Configurar evaluación'}
          </DialogTitle>
          <DialogDescription>
            {subjectLabel ? (
              <>
                Asignatura: <strong>{subjectLabel}</strong>
                {' · '}
                {EXAM_FOCUS_OPTIONS.find((option) => option.id === focus)?.label ??
                  selectedIntent?.subtitle ??
                  ''}
              </>
            ) : (
              EXAM_FOCUS_OPTIONS.find((option) => option.id === focus)?.description ??
              'Completa los parámetros antes de generar.'
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="sala-dialog__body ai-build-dialog">
          {/* Tipo de material vive en la barra lateral (estilo rúbrica). */}
          <div className="ai-build-dialog__grid">
            <div className="ai-build-dialog__form">
              <section className="ai-build-dialog__section">
                <p className="ai-build-dialog__section-title">Parámetros base</p>
                <SegmentedRow
                  label="Nivel educativo"
                  hint={EXAM_LEVEL_OPTIONS.find((option) => option.id === level)?.constraints}
                  value={level}
                  onChange={setLevel}
                  options={EXAM_LEVEL_OPTIONS.map((option) => ({
                    id: option.id,
                    label: option.label,
                    detail: option.constraints,
                  }))}
                />
                {isGuide && hasCurriculumSupport && (
                  <>
                    {inheritedCurriculum ? (
                      <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] leading-relaxed text-emerald-900">
                        Marco curricular heredado de la unidad planificada. La guía se alineará al OA,
                        OAH y OAA de la ruta.
                      </p>
                    ) : null}
                    {!inheritedCurriculum && curriculumSuggestionNote ? (
                      <p
                        className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] leading-relaxed text-sky-950"
                        role="status"
                      >
                        {curriculumSuggestionNote}
                      </p>
                    ) : null}
                    <GuideCurriculumPanel
                      level={level}
                      subjectTag={subjectTag}
                      generationMode={generationMode}
                      curriculumTarget={curriculumTarget}
                      onGenerationModeChange={setGenerationMode}
                      onCurriculumTargetChange={setCurriculumTarget}
                      onUserEdit={() => {
                        curriculumTouchedRef.current = true;
                        autoCurriculumAppliedRef.current = false;
                        setCurriculumSuggestionNote(null);
                      }}
                      onTraceChange={handleTraceChange}
                      hideModeToggle={inheritedCurriculum}
                      lockCurricular={inheritedCurriculum}
                    />
                  </>
                )}
                {isGuide && subjectTag && !hasCurriculumSupport ? (
                  <p
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-950"
                    role="status"
                  >
                    {curriculumUnavailableMessage(subjectTag)}. Generación en modo libre (sin OA
                    MINEDUC).
                  </p>
                ) : null}
                {!isGuide && inheritedCurriculum && curriculumTarget ? (
                  <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] leading-relaxed text-emerald-900">
                    Alineación curricular heredada: {curriculumTarget.oaCode}
                    {curriculumTarget.oahCode ? ` · OAH ${curriculumTarget.oahCode}` : ''}
                    {curriculumTarget.oaaCode ? ` · OAA ${curriculumTarget.oaaCode}` : ''}
                  </p>
                ) : null}
              </section>
              <section className="ai-build-dialog__section">
                <p className="ai-build-dialog__section-title">Contenido</p>
                {effectiveIsGuide ? (
                  <div className="block">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <span className="sala-label">
                          {isQuestionnaire
                            ? 'Qué van a practicar'
                            : hasCurriculumSupport
                              ? 'Intención y contexto'
                              : 'Tema o unidad'}
                        </span>
                        <span className="mt-0.5 block text-[10px] text-[var(--sala-ink-dim)]">
                          {isQuestionnaire
                            ? 'Simulación formativa: no califica. Describe el tema y el tipo de ítems para practicar.'
                            : hasCurriculumSupport
                              ? 'Metáfora, alcance numérico, actividades y cierre. El OA enmarca; aquí defines qué ocurre en el aula.'
                              : 'Describe el tema, unidad o consigna que guiará la generación.'}
                        </span>
                      </div>
                      {hasCurriculumSupport && generationMode === 'curricular' && (
                        <button
                          type="button"
                          className="ai-build-intent-suggest inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-[var(--sala-accent-neon)] disabled:pointer-events-none disabled:opacity-40 dark:hover:bg-slate-800/60"
                          disabled={!curricularReady || loading}
                          title={
                            curricularReady
                              ? 'Autocompletar intención desde el marco curricular seleccionado'
                              : 'Completa eje, OA, OAH y OAA para sugerir intención'
                          }
                          onClick={handleSuggestCurriculumIntent}
                        >
                          <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                          Sugerir intención
                        </button>
                      )}
                    </div>
                    {hasCurriculumSupport && GUIDE_INTENT_QUICK_PRESETS.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {GUIDE_INTENT_QUICK_PRESETS.map((preset) => (
                          <button
                            key={preset.id}
                            type="button"
                            className="sala-btn-outline py-1 text-[10px]"
                            onClick={() => setContentContext(preset.text)}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    )}
                    <textarea
                      ref={intentTextareaRef}
                      className="sala-input-dark mt-2 min-h-[6.5rem] w-full resize-y text-sm"
                      value={contentContext}
                      onChange={(e) => setContentContext(e.target.value)}
                      placeholder={
                        isQuestionnaire
                          ? 'Ej: Practicar tablas del 6 y 7 con ítems cortos — simulación, sin nota'
                          : intentPlaceholder
                      }
                    />
                  </div>
                ) : (
                  <label className="block">
                    <span className="sala-label">Tema o unidad</span>
                    <textarea
                      className="sala-input-dark mt-2 min-h-[6.5rem] w-full resize-y text-sm"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Ej: Operaciones básicas con enteros positivos — suma, resta y problemas contextualizados"
                    />
                  </label>
                )}
              </section>
                            {!effectiveIsGuide ? (
              <section className="ai-build-dialog__section">
                <p className="ai-build-dialog__section-title">Extensión</p>
                <SegmentedRow
                  label={effectiveIsInteractiveClass ? 'Duración' : 'Extensión'}
                  hint={`Se generarán exactamente ${selectedExtension.exactBlocks} bloques · ${selectedExtension.purpose}`}
                  value={extension}
                  onChange={setExtension}
                  options={EXAM_EXTENSION_OPTIONS.map((option) => ({
                    id: option.id,
                    icon: option.icon,
                    label: option.label,
                    detail: `${option.rangeLabel} — ${option.purpose}`,
                  }))}
                />
              </section>
              ) : null}
            </div>
            <aside className="ai-build-dialog__aside">
              <div className="ai-build-dialog__aside-panel space-y-4">
                <ExamAiConfigSidebar
                  focus={focus}
                  onFocusChange={handleFocusChange}
                  isGuide={effectiveIsGuide}
                  guideConfig={guideConfig}
                  onGuideLengthChange={(next) =>
                    setGuideConfig((current) => ({
                      ...current,
                      guideLength: next,
                    }))
                  }
                  questionnaireDelivery={questionnaireDelivery}
                  onQuestionnaireDeliveryChange={setQuestionnaireDelivery}
                  summaryHint={
                    effectiveIsGuide
                      ? isQuestionnaire
                        ? questionnaireDelivery === 'interactive'
                          ? 'Práctica simulada · luego podés lanzarla en sala digital'
                          : 'Práctica simulada · ficha imprimible (sin nota oficial)'
                        : guideLengthHint ||
                          `${effectiveBlockCount} bloques planificados · ${selectedGuideLength.detail}`
                      : `${selectedExtension.exactBlocks} bloques · ${selectedExtension.purpose}`
                  }
                />
                {effectiveIsGuide && open ? (
                  <SessionContextInferencePanel
                    foco={intentText || linkedSessionContext?.sessionObjective || ''}
                    evidencia={linkedSessionContext?.sessionSummary || ''}
                    title={linkedSessionContext?.sessionTitle || 'Guía'}
                    sessionConfig={
                      linkedSessionContext?.sessionConfig ?? { archetype: 'indagacion' }
                    }
                    subjectTag={subjectTag}
                    enabled={open}
                  />
                ) : null}
                {effectiveIsGuide && hasCurriculumSupport ? (
                  <GuidePlanningTrace
                    variant="sidebar"
                    trace={curriculumTrace}
                    blueprint={blueprintPreview}
                    loading={blueprintLoading}
                    generationMode={generationMode}
                    hasIntent={intentText.length >= 8}
                  />
                ) : null}
              </div>
            </aside>
          </div>
          {isGuide && !isMusic && (
            <GuideMaterialUploadPanel value={knowledgeBase} onChange={setKnowledgeBase} />
          )}
          {isMusic && (
            <>
              <div
                className={`rounded-lg border px-3 py-2 text-[10px] ${
                  isRepertoireMode
                    ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-100'
                    : 'border-amber-500/30 bg-amber-500/10 text-amber-100'
                }`}
              >
                {isRepertoireMode ? (
                  <>
                    <strong>Modo análisis de repertorio</strong> —{' '}
                    {isGuide
                      ? 'bloques 1–2 serán contexto y demostración guiada del compás real; el resto, práctica.'
                      : 'coacción estricta activada. La IA diseña preguntas sobre la partitura real; no inventa figuras ni compases.'}
                  </>
                ) : (
                  <>
                    <strong>Modo generativo puro</strong> — sin partitura. La IA crea ejercicios desde cero
                    con libertad creativa (solo rieles teóricos: duraciones, vocabulario chileno).
                  </>
                )}
              </div>
              <ScoreUploadPanel value={scoreAst} onChange={setScoreAst} musicPrefs={musicPrefs} />
              <MusicMethodologyPanel value={musicPrefs} onChange={setMusicPrefs} />
              <p className="text-[10px] text-[var(--sala-ink-dim)]">
                {isRepertoireMode
                  ? 'Sube .mid o .musicxml para activar el modo repertorio. Extensión y enfoque siguen siendo libres.'
                  : 'Opcional: sube un archivo para cambiar a modo repertorio.'}
              </p>
            </>
          )}
        </div>
        {!canSubmit && !loading ? (
          <p className="ai-build-dialog__validation" role="status">
            {!profileIdentityReady
              ? identityGate.message
              : intentText.length < 8
                ? 'Escribe al menos 8 caracteres en el tema o intención para continuar.'
                : hasCurriculumSupport && !curricularReady
                  ? generationMode === 'curricular' &&
                    curriculumTarget?.axis &&
                    curriculumTarget?.oaCode &&
                    (!curriculumTarget?.oahCode || !curriculumTarget?.oaaCode)
                    ? 'Selecciona la habilidad (OAH) y la actitud (OAA) para completar el escudo curricular.'
                    : 'Selecciona eje, OA, OAH y OAA del marco curricular.'
                  : 'Completa los campos requeridos para generar.'}
          </p>
        ) : null}
        <DialogFooter>
          <button type="button" className="sala-btn-outline text-xs" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </button>
          <button
            type="button"
            className="sala-btn-hero text-xs"
            disabled={!canSubmit}
            onClick={handleConfirm}
          >
            {loading
              ? 'Generando…'
              : isGuide
                ? `Generar guía · ${effectiveBlockCount} bloques`
                : isInteractiveClass
                  ? `Generar clase · ${selectedExtension.exactBlocks} diapositivas`
                  : `Generar ${selectedExtension.exactBlocks} bloques`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
